import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Swords,
  Trophy,
  Users,
  Flame,
  Zap,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Crown,
  Medal
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHALLENGE_TYPES = [
  {
    id: "workout_race",
    name: "Workout Race",
    description: "Most workouts completed wins!",
    icon: Target,
    color: "text-blue-500",
    bg: "bg-blue-500/20"
  },
  {
    id: "streak_battle",
    name: "Streak Battle",
    description: "Longest streak takes the crown!",
    icon: Flame,
    color: "text-orange-500",
    bg: "bg-orange-500/20"
  },
  {
    id: "beast_mode_duel",
    name: "Beast Mode Duel",
    description: "Most Beast Mode activations wins!",
    icon: Zap,
    color: "text-red-500",
    bg: "bg-red-500/20"
  }
];

export default function PartnerChallenges() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState({
    pending_invites: [],
    active_challenges: [],
    completed: [],
    sent_invites: []
  });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState("");
  const [selectedType, setSelectedType] = useState("workout_race");
  const [duration, setDuration] = useState(7);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/partner/challenges`);
      setChallenges(response.data);
    } catch (error) {
      toast.error("Failed to load challenges");
    } finally {
      setLoading(false);
    }
  };

  const createChallenge = async () => {
    if (!partnerEmail) {
      toast.error("Enter your partner's email");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/partner/challenge/create`, {
        partner_email: partnerEmail,
        challenge_type: selectedType,
        duration_days: duration
      });
      toast.success(response.data.message);
      setShowCreate(false);
      setPartnerEmail("");
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create challenge");
    } finally {
      setCreating(false);
    }
  };

  const acceptChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/partner/challenge/accept/${challengeId}`);
      toast.success("Challenge accepted! GAME ON!");
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept challenge");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Swords className="h-12 w-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-primary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-primary" />
              <span className="font-bold tracking-tight">PARTNER CHALLENGES</span>
            </div>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-primary hover:bg-primary/90"
              size="sm"
            >
              <Users className="h-4 w-4 mr-2" /> Challenge
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
            <Swords className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tighter mb-2">
            Partner <span className="text-primary">Challenges</span>
          </h1>
          <p className="text-muted-foreground">
            Challenge your partner, compete together, grow stronger!
          </p>
        </motion.div>

        {/* Pending Invites */}
        {challenges.pending_invites.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h2 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" /> Incoming Challenges
            </h2>
            <div className="space-y-3">
              {challenges.pending_invites.map((challenge) => (
                <Card key={challenge.id} className="border-yellow-500/50 bg-yellow-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold">{challenge.challenger_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {CHALLENGE_TYPES.find(t => t.id === challenge.challenge_type)?.name} • {challenge.duration_days} days
                        </p>
                      </div>
                      <Button
                        onClick={() => acceptChallenge(challenge.id)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                      >
                        ACCEPT
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Active Challenges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h2 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" /> Active Battles
          </h2>
          
          {challenges.active_challenges.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active challenges</p>
                <Button
                  onClick={() => setShowCreate(true)}
                  variant="outline"
                  className="mt-4"
                >
                  Start a Challenge
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {challenges.active_challenges.map((challenge) => {
                const isChallenger = challenge.challenger_id === user?.id;
                const myScore = challenge.scores[user?.id] || {};
                const opponentScore = challenge.scores[isChallenger ? challenge.partner_id : challenge.challenger_id] || {};
                const opponentName = isChallenger ? challenge.partner_name : challenge.challenger_name;
                const typeConfig = CHALLENGE_TYPES.find(t => t.id === challenge.challenge_type);
                const TypeIcon = typeConfig?.icon || Target;
                
                return (
                  <Card key={challenge.id} className="border-primary/30 overflow-hidden">
                    <div className={`h-1 ${typeConfig?.bg?.replace('/20', '')}`} />
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`h-5 w-5 ${typeConfig?.color}`} />
                          <span className="font-bold">{typeConfig?.name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Ends: {challenge.end_date}
                        </span>
                      </div>
                      
                      {/* Score Comparison */}
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-black text-primary">
                            {myScore.workouts || 0}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase">You</p>
                        </div>
                        <div className="flex items-center justify-center">
                          <span className="text-2xl font-black text-muted-foreground">VS</span>
                        </div>
                        <div>
                          <p className="text-2xl font-black text-orange-500">
                            {opponentScore.workouts || 0}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase">{opponentName}</p>
                        </div>
                      </div>
                      
                      {/* Beast Modes */}
                      <div className="mt-4 flex justify-center gap-6 text-sm">
                        <div className="flex items-center gap-1">
                          <Flame className="h-4 w-4 text-red-500" />
                          <span>{myScore.beast_modes || 0} Beast Modes</span>
                        </div>
                        <div className="text-muted-foreground">vs</div>
                        <div className="flex items-center gap-1">
                          <Flame className="h-4 w-4 text-orange-500" />
                          <span>{opponentScore.beast_modes || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Completed Challenges */}
        {challenges.completed.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" /> Battle History
            </h2>
            <div className="space-y-3">
              {challenges.completed.slice(0, 5).map((challenge) => {
                const isWinner = challenge.winner_id === user?.id;
                const opponentName = challenge.challenger_id === user?.id 
                  ? challenge.partner_name 
                  : challenge.challenger_name;
                
                return (
                  <Card 
                    key={challenge.id} 
                    className={`border-border ${isWinner ? 'bg-yellow-500/5 border-yellow-500/30' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isWinner ? (
                            <Crown className="h-6 w-6 text-yellow-500" />
                          ) : challenge.winner_id ? (
                            <Medal className="h-6 w-6 text-muted-foreground" />
                          ) : (
                            <Users className="h-6 w-6 text-muted-foreground" />
                          )}
                          <div>
                            <p className="font-bold">
                              {isWinner ? "Victory!" : challenge.winner_id ? "Defeat" : "Draw"} vs {opponentName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {CHALLENGE_TYPES.find(t => t.id === challenge.challenge_type)?.name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">
                            {challenge.final_scores?.challenger_points || 0} - {challenge.final_scores?.partner_points || 0}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        )}
      </main>

      {/* Create Challenge Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
            onClick={() => setShowCreate(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="bg-card border-primary">
                <CardHeader>
                  <CardTitle className="text-center uppercase tracking-tighter">
                    <Swords className="h-8 w-8 text-primary mx-auto mb-2" />
                    Create Challenge
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Partner Email */}
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                      Partner's Email
                    </label>
                    <Input
                      type="email"
                      placeholder="partner@email.com"
                      value={partnerEmail}
                      onChange={(e) => setPartnerEmail(e.target.value)}
                      className="bg-secondary border-border"
                    />
                  </div>
                  
                  {/* Challenge Type */}
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                      Challenge Type
                    </label>
                    <div className="grid gap-2">
                      {CHALLENGE_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.id}
                            onClick={() => setSelectedType(type.id)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedType === type.id
                                ? `${type.bg} border-primary`
                                : 'bg-secondary border-border hover:border-primary/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Icon className={`h-5 w-5 ${type.color}`} />
                              <div>
                                <p className="font-bold text-sm">{type.name}</p>
                                <p className="text-xs text-muted-foreground">{type.description}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Duration */}
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 block">
                      Duration
                    </label>
                    <div className="flex gap-2">
                      {[7, 14, 30].map((days) => (
                        <button
                          key={days}
                          onClick={() => setDuration(days)}
                          className={`flex-1 py-2 rounded-lg border font-bold ${
                            duration === days
                              ? 'bg-primary text-white border-primary'
                              : 'bg-secondary border-border hover:border-primary/50'
                          }`}
                        >
                          {days} Days
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={createChallenge}
                    disabled={creating || !partnerEmail}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest"
                  >
                    {creating ? "Sending..." : (
                      <>
                        <Send className="h-4 w-4 mr-2" /> Send Challenge
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
